export type CompetitionType = 'OLYMPIAD' | 'SPORT';
export type CompetitionStatus =
  | 'PLANNED'
  | 'REGISTRATION_OPEN'
  | 'REGISTRATION_CLOSED'
  | 'FINISHED'
  | 'CANCELLED';

export interface ClassYear {
  id: number;
  name: string;
  class_number: number;
}

export interface Competition {
  id: number;
  name: string;
  type: CompetitionType;
  status: CompetitionStatus;
  start_date: string | null;
  end_date: string | null;
  /** Purpose (CONTEXT.md): why it is held. */
  purpose: string | null;
  /** Sanitised HTML from tsum's editor. */
  description: string | null;
  /** Tips (CONTEXT.md): short advice, one per item. */
  tips: string[];
  location: string | null;
  /** The forms.mn registration form: https://forms.mn/f/<slug>. */
  forms_slug: string | null;
  season_name: string | null;
  /** Registration open or closed: the card opens a Participant lookup. */
  can_lookup: boolean;
  /** Results (CONTEXT.md): tsum serves them via competition-results while on. */
  results_published: boolean;
  class_years: ClassYear[];
}

/** One scored Participant: name and school only, never contact details. */
export interface ResultRow {
  last_name: string | null;
  first_name: string;
  school_name: string | null;
  total_score: number;
  /** Award level, e.g. "Алт"; null when none was given. */
  award: string | null;
  /** 1, 1, 1, 2…: equal totals share a place, the next total takes the next one. */
  place: number;
}

export interface Results {
  /**
   * Per Competition class year: its top Participants by Total score (as many
   * as the Competition shows, ties at the last place included), highest first.
   */
  class_years: { id: number; name: string; max_score: number; results: ResultRow[] }[];
}

export interface ParticipantResult {
  last_name: string | null;
  first_name: string;
  school_name: string | null;
  class_year_name: string | null;
  exam_room_number: string | null;
  exam_number: string | null;
  start_at: string | null;
  end_at: string | null;
  arrival_at: string | null;
}

export interface SearchResult {
  competition: Competition;
  participants: ParticipantResult[];
}

export type LookupResult = { ok: true; result: SearchResult } | { ok: false; error: string };

export const TYPE_LABELS: Record<CompetitionType, string> = {
  OLYMPIAD: 'Олимпиад',
  SPORT: 'Спорт',
};

export const STATUS_LABELS: Record<CompetitionStatus, { label: string; className: string }> = {
  PLANNED: { label: 'Төлөвлөсөн', className: 'bg-yellow-100 text-yellow-900' },
  CANCELLED: { label: 'Цуцлагдсан', className: 'bg-red-50 text-red-700' },
  REGISTRATION_OPEN: { label: 'Бүртгэл нээлттэй', className: 'bg-green-100 text-green-700' },
  REGISTRATION_CLOSED: { label: 'Бүртгэл хаагдсан', className: 'bg-yellow-200 text-yellow-900' },
  FINISHED: { label: 'Дууссан', className: 'bg-gray-100 text-gray-600' },
};

// Mongolian mobile numbers are 8 digits.
export const MOBILE_PATTERN = /^\d{8}$/;
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
